import { DataProps } from '@diamond/shared/types';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  activate,
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  RemoteConfig,
} from 'firebase/remote-config';

import { buyerCaptureException } from '../sentry-capture-exception';

export const firebaseConfig = {
  apiKey: process.env['NX_PUBLIC_FIREBASE_API_KEY'] || '',
  authDomain: process.env['NX_PUBLIC_FIREBASE_AUTH_DOMAIN'] || '',
  projectId: process.env['NX_PUBLIC_FIREBASE_PROJECT_ID'] || '',
  storageBucket: process.env['NX_PUBLIC_FIREBASE_STORAGE_BUCKET'] || '',
  messagingSenderId:
    process.env['NX_PUBLIC_FIREBASE_MESSAGING_SENDER_ID'] || '',
  appId: process.env['NX_PUBLIC_FIREBASE_APP_ID'] || '',
  measurementId: process.env['NX_PUBLIC_FIREBASE_MEASUREMENT_ID'] || '',
};

let firebaseAppInstance: FirebaseApp | undefined;
let remoteConfigInstance: RemoteConfig | undefined;

export const getFirebaseApp = (): FirebaseApp | undefined => {
  if (firebaseAppInstance) {
    return firebaseAppInstance;
  }

  try {
    firebaseAppInstance = initializeApp();
    console.log('Retrieved existing Firebase app instance');
  } catch (error) {
    if (firebaseConfig.apiKey !== '') {
      try {
        firebaseAppInstance = initializeApp(firebaseConfig);
        console.log('Firebase app initialized successfully with config');
      } catch (initError) {
        console.error('Failed to initialize Firebase app', initError);
      }
    } else {
      console.warn('Cannot initialize Firebase app - no API key provided');
    }
  }

  return firebaseAppInstance;
};

const getFirebaseRemoteConfig = (): RemoteConfig | undefined => {
  if (remoteConfigInstance) {
    return remoteConfigInstance;
  }

  const app = getFirebaseApp();
  if (!app) {
    console.warn('Cannot initialize Remote Config - no Firebase app available');
    return undefined;
  }

  try {
    remoteConfigInstance = getRemoteConfig(app);
    remoteConfigInstance.defaultConfig = DEFAULT_CONFIG_VALUES;
    remoteConfigInstance.settings.minimumFetchIntervalMillis = 60000; // 1 min
    remoteConfigInstance.settings.fetchTimeoutMillis = 60000; // 1 minute
    return remoteConfigInstance;
  } catch (error) {
    console.error('Failed to initialize Remote Config', error);
    return undefined;
  }
};

const DEFAULT_CONFIG_VALUES = {
  feature_slider_enabled: true,
  maintenance_mode: false,
  maintenance_message: "We're currently under maintenance. We'll be back soon!",
  ff_web_test: 'Default test value - not fetched from Firebase',
  slider_throttle_time: 1000,
  ff_web_promo_highlight: false,
};

export const initializeRemoteConfig = async (): Promise<
  RemoteConfig | undefined
> => {
  const configInstance = getFirebaseRemoteConfig();
  if (!configInstance) {
    console.warn('Cannot initialize Remote Config - no instance available');
    return undefined;
  }

  try {
    const fetchAndActivateResult = await fetchAndActivate(configInstance);

    if (!fetchAndActivateResult) {
      await activate(configInstance);
    }

    return configInstance;
  } catch (error) {
    const err = error as DataProps;
    if (process.env.NODE_ENV === 'production') {
      buyerCaptureException(err, (scope) => {
        scope.setTag(
          'Firebase Remote Config',
          'error initializing remote config'
        );
        scope.setLevel('error');
      });
    }
    return configInstance;
  }
};

export const getRemoteConfigBoolean = (key: string): boolean => {
  const configInstance = getFirebaseRemoteConfig();
  if (!configInstance) {
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as boolean) || false
    );
  }

  try {
    const value = getValue(configInstance, key);
    return value.asBoolean();
  } catch (error) {
    console.error(`Error getting remote config value for ${key}:`, error);
    const err = error as DataProps;

    if (process.env.NODE_ENV === 'production') {
      buyerCaptureException(err, (scope) => {
        scope.setTag(
          `Firebase Remote Config - ${key},`,
          'error get remote config boolean'
        );
        scope.setLevel('error');
      });
    }
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as boolean) || false
    );
  }
};

export const getRemoteConfigString = (key: string): string => {
  const configInstance = getFirebaseRemoteConfig();
  if (!configInstance) {
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as string) || ''
    );
  }

  try {
    const value = getValue(configInstance, key);
    return value.asString();
  } catch (error) {
    console.error(`Error getting remote config value for ${key}:`, error);
    const err = error as DataProps;
    if (process.env.NODE_ENV === 'production') {
      buyerCaptureException(err, (scope) => {
        scope.setTag(
          `Firebase Remote Config - ${key},`,
          'error get remote config string'
        );
        scope.setLevel('error');
      });
    }
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as string) || ''
    );
  }
};

export const getRemoteConfigNumber = (key: string): number => {
  const configInstance = getFirebaseRemoteConfig();
  if (!configInstance) {
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as unknown as number) || 0
    );
  }

  try {
    const value = getValue(configInstance, key);
    return value.asNumber();
  } catch (error) {
    console.error(`Error getting remote config value for ${key}:`, error);
    const err = error as DataProps;

    buyerCaptureException(err, (scope) => {
      scope.setTag(
        `Firebase Remote Config - ${key},`,
        'error get remote config boolean'
      );
      scope.setLevel('error');
    });
    return (
      (DEFAULT_CONFIG_VALUES[
        key as keyof typeof DEFAULT_CONFIG_VALUES
      ] as unknown as number) || 0
    );
  }
};
